import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
import {
    Plus,
    Search,
    Edit,
    Trash2,
    Copy,
    Loader2,
    X,
    Shield,
    Check,
    Power
} from 'lucide-react'
import toast from 'react-hot-toast'
import ConfirmModal from '../../components/ConfirmModal'
import './ProfilsPage.css'

export default function ProfilsPage() {
    const { t } = useTranslation()
    const { hasPermission, user } = useAuthStore()

    const [profils, setProfils] = useState([])
    const [permissionGroups, setPermissionGroups] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')

    const [showModal, setShowModal] = useState(false)
    const [editingProfil, setEditingProfil] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)

    const [formData, setFormData] = useState({
        nom_profil: '',
        code_profil: '',
        description: '',
        permissions: []
    })

    // Get current user's profile ID
    const currentUserProfilId = user?.profil?._id

    useEffect(() => {
        fetchProfils()
        fetchPermissions()
    }, [])

    const fetchProfils = async () => {
        setLoading(true)
        try {
            const response = await api.get('/profils', { params: { all: true } })
            setProfils(response.data.data.profils)
        } catch (error) {
            toast.error(t('common.error'))
        } finally {
            setLoading(false)
        }
    }

    const fetchPermissions = async () => {
        try {
            const response = await api.get('/profils/permissions')
            // Ensure we have an array
            setPermissionGroups(Array.isArray(response.data.data.groups) ? response.data.data.groups : [])
        } catch (error) {
            console.error('Error fetching permissions:', error)
        }
    }

    const openAddModal = () => {
        setEditingProfil(null)
        setFormData({
            nom_profil: '',
            code_profil: '',
            description: '',
            permissions: []
        })
        setShowModal(true)
    }

    const openEditModal = (profil) => {
        setEditingProfil(profil)
        setFormData({
            nom_profil: profil.nom_profil,
            code_profil: profil.code_profil,
            description: profil.description || '',
            permissions: profil.permissions || []
        })
        setShowModal(true)
    }

    const openDuplicateModal = (profil) => {
        setEditingProfil(null) // Treat as new
        setFormData({
            nom_profil: `${profil.nom_profil} (copie)`,
            code_profil: `${profil.code_profil}_COPY`,
            description: profil.description || '',
            permissions: profil.permissions || []
        })
        setShowModal(true)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)

        try {
            const payload = { ...formData }
            // Auto-generate code if creating and not present/empty (though we hide input, it might be safer)
            if (!editingProfil && payload.nom_profil) {
                // Auto-generate code from name (uppercase, replace spaces/specials with _)
                payload.code_profil = payload.nom_profil
                    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove accents
                    .toUpperCase()
                    .replace(/[^A-Z0-9]+/g, '_') // Replace non-alphanumeric with _
                    .replace(/^_+|_+$/g, ''); // Trim _
            }

            if (editingProfil) {
                await api.put(`/profils/${editingProfil._id}`, formData)
            } else {
                await api.post('/profils', payload)
            }
            toast.success(t('common.success'))
            setShowModal(false)
            fetchProfils()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        } finally {
            setModalLoading(false)
        }
    }

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        profil: null,
        action: null, // 'delete' or 'toggle'
        loading: false
    })

    const handleDelete = (profil) => {
        setConfirmModal({ open: true, profil, action: 'delete', loading: false })
    }

    const handleToggleStatus = (profil) => {
        setConfirmModal({ open: true, profil, action: 'toggle', loading: false })
    }

    const confirmAction = async () => {
        const { profil, action } = confirmModal
        if (!profil || !action) return

        setConfirmModal(prev => ({ ...prev, loading: true }))

        try {
            if (action === 'delete') {
                await api.delete(`/profils/${profil._id}`)
            } else if (action === 'toggle') {
                const newStatus = profil.etat === 'ACTIF' ? 'SUPPRIME' : 'ACTIF'
                await api.put(`/profils/${profil._id}`, { etat: newStatus })
            }
            toast.success(t('common.success'))
            setConfirmModal({ open: false, profil: null, action: null, loading: false })
            fetchProfils()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
            setConfirmModal(prev => ({ ...prev, loading: false }))
        }
    }

    const getConfirmModalProps = () => {
        const { profil, action } = confirmModal
        if (action === 'delete') {
            return {
                title: t('profils.deleteConfirm'),
                message: profil?.nom_profil,
                confirmText: t('common.delete'),
                type: 'danger'
            }
        } else if (action === 'toggle') {
            const isActivating = profil?.etat === 'SUPPRIME'
            return {
                title: isActivating ? t('profils.activateConfirm') : t('profils.deactivateConfirm'),
                message: profil?.nom_profil,
                confirmText: isActivating ? t('profils.activate') : t('profils.deactivate'),
                type: isActivating ? 'success' : 'warning'
            }
        }
        return {}
    }

    const togglePermission = (code) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(code)
                ? prev.permissions.filter(p => p !== code)
                : [...prev.permissions, code]
        }))
    }

    const toggleGroupPermissions = (group) => {
        const allSelected = group.permissions.every(p => formData.permissions.includes(p.code))

        if (allSelected) {
            setFormData(prev => ({
                ...prev,
                permissions: prev.permissions.filter(p => !group.permissions.find(gp => gp.code === p))
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                permissions: [...new Set([...prev.permissions, ...group.permissions.map(p => p.code)])]
            }))
        }
    }

    const filteredProfils = profils.filter(p =>
        p.nom_profil.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.code_profil.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Check if user can modify/delete a profile (not their own profile)
    const canModifyProfile = (profil) => {
        return profil._id !== currentUserProfilId && profil.code_profil !== 'SUPERADMIN'
    }

    const canDeleteProfile = (profil) => {
        return profil._id !== currentUserProfilId && !profil.undeletable
    }

    return (
        <div className="profils-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('profils.title')}</h1>
                    <p className="page-subtitle">{profils.length} profils</p>
                </div>

                {hasPermission('ADD_PROFILE') && (
                    <button className="btn btn-primary" onClick={openAddModal}>
                        <Plus size={18} />
                        {t('profils.add')}
                    </button>
                )}
            </div>

            {/* Search */}
            <div className="card">
                <div className="filters-bar">
                    <div className="search-form">
                        <div className="search-input-wrapper">
                            <Search size={18} className="search-icon" />
                            <input
                                type="text"
                                className="form-input search-input"
                                placeholder={t('common.search') + '...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Profils Grid */}
            {loading ? (
                <div className="profils-loading">
                    <Loader2 size={32} className="spin" />
                </div>
            ) : (
                <div className="profils-grid">
                    {filteredProfils.map((profil) => (
                        <div
                            key={profil._id}
                            className={`profil-card card ${profil.etat === 'SUPPRIME' ? 'profil-inactive' : ''}`}
                        >
                            <div className="profil-card-header">
                                <div className="profil-icon">
                                    <Shield size={24} />
                                </div>
                                <div className="profil-info">
                                    <h3>{profil.nom_profil}</h3>
                                    <code>{profil.code_profil}</code>
                                </div>
                                <div className="profil-badges">
                                    {profil.undeletable && (
                                        <span className="badge badge-info">{t('profils.system')}</span>
                                    )}
                                    {profil._id === currentUserProfilId && (
                                        <span className="badge badge-warning">{t('profils.myProfile')}</span>
                                    )}
                                    {profil.etat === 'SUPPRIME' && (
                                        <span className="badge badge-error">{t('profils.inactive')}</span>
                                    )}
                                </div>
                            </div>

                            {profil.description && (
                                <p className="profil-description">{profil.description}</p>
                            )}

                            <div className="profil-permissions">
                                <span className="permissions-count">
                                    {profil.permissions?.includes('*')
                                        ? t('profils.allPermissions')
                                        : `${profil.permissions?.length || 0} permissions`
                                    }
                                </span>
                            </div>

                            <div className="profil-actions">
                                {hasPermission('MODIF_PROFIL') && canModifyProfile(profil) && (
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => openEditModal(profil)}
                                    >
                                        <Edit size={16} />
                                        {t('common.edit')}
                                    </button>
                                )}
                                {hasPermission('ADD_PROFILE') && (
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => openDuplicateModal(profil)}
                                    >
                                        <Copy size={16} />
                                        {t('common.duplicate')}
                                    </button>
                                )}
                                {hasPermission('DELETE_PROFILE') && canDeleteProfile(profil) && (
                                    <>
                                        <button
                                            className={`btn btn-ghost btn-sm ${profil.etat === 'SUPPRIME' ? 'btn-success-ghost' : 'btn-warning-ghost'}`}
                                            onClick={() => handleToggleStatus(profil)}
                                            title={profil.etat === 'SUPPRIME' ? t('profils.activate') : t('profils.deactivate')}
                                        >
                                            <Power size={16} />
                                        </button>
                                        {profil.etat === 'ACTIF' && (
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => handleDelete(profil)}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Edit/Add Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingProfil ? t('profils.edit') : t('profils.add')}
                            </h3>
                            <button className="btn btn-icon btn-ghost" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">{t('profils.name')} *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.nom_profil}
                                        onChange={(e) => setFormData({ ...formData, nom_profil: e.target.value })}
                                        required
                                        placeholder="Ex: Responsable RH"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">{t('profils.description')}</label>
                                    <textarea
                                        className="form-textarea"
                                        rows={2}
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">{t('profils.permissions')}</label>
                                    <div className="permissions-editor" style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1fr) minmax(250px, 1fr)', gap: '1rem' }}>
                                        {permissionGroups.map((group) => (
                                            <div key={group.key} className="permission-group" style={{ marginBottom: '1rem', border: '1px solid #ddd', borderRadius: '0.5rem', padding: '0.75rem' }}>
                                                <div
                                                    className="permission-group-header"
                                                    onClick={() => toggleGroupPermissions(group)}
                                                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '0.5rem' }}
                                                >
                                                    <div className={`permission-checkbox ${group.permissions.every(p => formData.permissions.includes(p.code)) ? 'checked' : ''
                                                        }`} style={{ marginRight: '0.5rem', width: 16, height: 16, border: '1px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 3 }}>
                                                        {group.permissions.every(p => formData.permissions.includes(p.code)) && <Check size={12} />}
                                                    </div>
                                                    <strong>{group.label}</strong>
                                                </div>
                                                <div className="permission-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingLeft: '1.5rem' }}>
                                                    {group.permissions.map((perm) => (
                                                        <label key={perm.code} className="permission-item" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.permissions.includes(perm.code)}
                                                                onChange={() => togglePermission(perm.code)}
                                                                style={{ marginRight: '0.5rem' }}
                                                            />
                                                            <span style={{ fontSize: '0.9rem' }}>{perm.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                                    {modalLoading ? <Loader2 size={18} className="spin" /> : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal({ open: false, profil: null, action: null, loading: false })}
                onConfirm={confirmAction}
                cancelText={t('common.cancel')}
                loading={confirmModal.loading}
                {...getConfirmModalProps()}
            />
        </div>
    )
}
